import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '../../../../../design-system/chart/chart';
import { getAmountWithCurrency } from '../../../../../utils/get-amount-with-currency';
import { getCurrencySymbol } from '../../../../../utils/get-currency-symbol';
import { VendorFinancials } from '../../../utils/get-vendor-financials';

type TransactionChartProps = {
  data: VendorFinancials;
};

export const TransactionChart = ({ data }: TransactionChartProps) => {
  const chartConfig = {
    moneyIn: {
      label: 'Money in',
      color: '#3F9B21',
    },
    moneyOut: {
      label: 'Money out',
      color: '#000000',
    },
  } satisfies ChartConfig;

  const pastSixMonths = data.monthlyTotals.slice(-6);

  const hasMoneyIn = pastSixMonths.some((item) => item.moneyIn !== 0);
  const hasMoneyOut = pastSixMonths.some((item) => item.moneyOut !== 0);

  // Format y-axis labels with K/M suffixes and currency symbol
  const formatYAxisLabel = (value: number) => {
    const absValue = Math.abs(value);
    const currencySymbol = getCurrencySymbol(data.currency);

    if (absValue >= 1000000) {
      return `${currencySymbol}${(value / 1000000).toFixed(1)}m`;
    } else if (absValue >= 1000) {
      return `${currencySymbol}${(value / 1000).toFixed(1)}k`;
    }

    return `${currencySymbol}${value}`;
  };

  return (
    <ChartContainer config={chartConfig} className="min-h-[330px] w-full py-7">
      <BarChart accessibilityLayer data={pastSixMonths} barCategoryGap={1} barGap={2} barSize={22} maxBarSize={22}>
        <CartesianGrid vertical={false} strokeDasharray="4" />

        <XAxis dataKey="month" tickLine={false} tickMargin={10} axisLine={false} />
        <YAxis tickFormatter={(value) => formatYAxisLabel(value as number)} width={50} />

        <ChartTooltip
          content={
            <ChartTooltipContent
              hideLabel
              formatter={(value, name) => {
                const label = chartConfig[name as keyof typeof chartConfig]?.label || name;
                const amount = getAmountWithCurrency({
                  amount: value as number,
                  currency: data.currency,
                  isAccountBalance: true,
                });
                return [`${label} ${amount}`];
              }}
            />
          }
        />
        {hasMoneyOut && <Bar dataKey="moneyOut" fill={chartConfig.moneyOut.color} radius={2} />}
        {hasMoneyIn && <Bar dataKey="moneyIn" fill={chartConfig.moneyIn.color} radius={2} />}
      </BarChart>
    </ChartContainer>
  );
};
